// A live-rig bundle: every active region re-decodes off ONE control vector, presented once.
// Notes: ~/comments/aos-threejs-poc/LiveRigPipeline_over_the_engine.md

import type { OGSBranch } from "../graph/nodes/OGSBranch.ts";
import type { PartPoser } from "./PartRigPipeline.ts";
import { partEnabled, type RenderPlan } from "./renderPlan.ts";
import { prefersFp16, type OgsPrecision } from "./precision.ts";
import type { RigConstraintSet } from "./rigConstraints.ts";
import type { OgsPipeline, PipelineDebug, PipelineReady } from "./types.ts";
import { createSilentLogger, type OgsLogger } from "./ogsLog.ts";

/** A branch whose rig was REFUSED as not fitting this bundle's mesh. */
export interface RefusedRig { name: string; error: string }

/** The slice of HeadTeethEngine this pipeline drives. */
export interface LiveRigEngine {
  readonly rigDim: number;
  calibrate(baseRig: Float32Array | null): Promise<RefusedRig[] | undefined>;
  setIdleRig(rig: Float32Array | null): void;
  baseRig(): Float32Array;
  setRegions(regions: Record<string, boolean>): void;
  runFull(rig: Float32Array): Promise<void>;
  /** Re-present at externally posed VERTICES, reusing every branch's cached decode. */
  runPosed(pose: () => OGSBranch[] | void): Promise<void>;
  runAppearance(): Promise<void>;
  setPrecision(preferFp16: boolean): Promise<void>;
}

export interface LiveRigPipelineOptions {
  engine: LiveRigEngine;
  branches: OGSBranch[];
  constraints: RigConstraintSet;
  setVisible: (visible: boolean) => void;
  /** Reported when a branch's rig does not fit — the asset is present and WRONG. */
  onRefusedRigs?: (refused: RefusedRig[]) => void;
  precision: OgsPrecision;
  log?: OgsLogger;
  worldScale?: number;
}

export class LiveRigPipeline implements OgsPipeline {
  readonly kind = "live-rig" as const;

  private readonly engine: LiveRigEngine;
  private readonly branches: OGSBranch[];
  private readonly constraints: RigConstraintSet;
  private readonly setVisibleFn: (visible: boolean) => void;
  private readonly onRefusedRigs?: (refused: RefusedRig[]) => void;
  private readonly log: OgsLogger;
  private readonly worldScale: number;

  private precision: OgsPrecision;
  private regionOn: Record<string, boolean>;
  private refused: RefusedRig[] = [];
  private pushes = 0;
  private disposed = false;
  /** Whoever poses the part rig — a MIXED bundle's body. Absent on a head-only one. */
  private poser: PartPoser | null = null;
  /** Applied but not yet drawn; promoted by notePresented. */
  private appliedTime: number | null = null;
  private landed: number | null = null;

  constructor(options: LiveRigPipelineOptions) {
    this.engine = options.engine;
    this.branches = options.branches;
    this.constraints = options.constraints;
    this.setVisibleFn = options.setVisible;
    this.onRefusedRigs = options.onRefusedRigs;
    this.precision = options.precision;
    this.log = options.log ?? createSilentLogger();
    this.worldScale = options.worldScale ?? 1;
    this.regionOn = Object.fromEntries(this.branches.map((branch) => [branch.name, true]));
  }

  /**
   * Anchor every branch, then decode the rest pose.
   *
   * The ORDER matters twice. `calibrate` must run at the rig whose baked mesh IS
   * `neutral_vertices` — row 0 — or the correction is measured at the wrong pose. The first
   * DECODE must then use the same vector the live path composes onto, or the opening frames
   * render at one pose and snap to another on the first ARKit update.
   */
  async init(): Promise<PipelineReady> {
    const refused = await this.engine.calibrate(this.constraints.restRig) ?? [];
    this.refused = refused;
    if (refused.length) {
      // The one rig failure the loader cannot see: the pack was present and parsed, and
      // only the fit knows it belongs to a different character.
      this.log.error(`rig REFUSED for ${refused.map((r) => `${r.name}: ${r.error}`).join("; ")}`);
      this.onRefusedRigs?.(refused);
    }
    this.engine.setIdleRig(this.constraints.arkitBase);
    if (this.disposed) return { count: 0, lines: [] };
    await this.engine.runFull(this.engine.baseRig());
    return {
      count: 0,
      lines: [`live head+teeth pipeline ready (${this.branches.length} branches, `
        + `rigDim=${this.engine.rigDim}, worldScale=${this.worldScale})`],
    };
  }

  /** A face-graph push. The engine coalesces: the latest rig wins, none are queued. */
  setRig(rig: Float32Array): void {
    if (this.disposed) return;
    this.pushes++;
    void this.engine.runFull(rig).catch((error) => this.log.error("rig decode failed:", error));
  }

  /** Install the body drive's poser, or clear it. */
  setPoser(poser: PartPoser | null): void {
    this.poser = poser;
  }

  /**
   * The body drive's per-frame call: pose the part rig and re-present at the new vertices,
   * reusing every branch's cached decode — body motion changes geometry, not appearance.
   *
   * `null` means nobody poses the rig this frame. Unlike the part-rig pipeline there is no
   * pending recolour to run here: this engine coalesces its own camera passes.
   */
  driveAt(t: number | null): boolean {
    if (this.disposed || t == null || !this.poser) return false;
    const poser = this.poser;
    void this.engine.runPosed(() => {
      const anchored = poser.pose(t) ?? [];
      // Applied, NOT yet drawn. A pose superseded by a newer one never runs this callback,
      // so its time is never claimed — which is what keeps the two honest.
      this.appliedTime = t;
      return anchored;
    }).catch((error) => this.log.error("posed frame failed:", error));
    return true;
  }

  /** The shell calls this from the engine's onPresented: a pose is now ON SCREEN. */
  notePresented(): void {
    if (this.appliedTime === null) return;
    this.landed = this.appliedTime;
    this.appliedTime = null;
  }

  /** The clip time of the pose that reached the screen — the root is placed at this. */
  get landedTime(): number | null { return this.landed; }

  runAppearance(): void {
    if (this.disposed) return;
    void this.engine.runAppearance().catch((error) => this.log.error("recolour failed:", error));
  }

  /** Is a light change worth a re-present? Only a PBR branch renders under the scene's light. */
  get followsLight(): boolean {
    return this.branches.some((branch) => !!branch.lifter?.pbrShade);
  }

  setVisible(visible: boolean): void {
    this.setVisibleFn(visible);
  }

  /** Region toggles and the decoder-precision pick — both live, no reload. */
  applyPlan(next: RenderPlan, _prev: RenderPlan | null): void {
    if (this.disposed) return;
    const regions = Object.fromEntries(this.branches.map((branch) => [branch.name, partEnabled(next, branch.name)]));
    const changed = this.branches.some((branch) => this.regionOn[branch.name] !== regions[branch.name]);
    if (changed) {
      for (const branch of this.branches) {
        if (this.regionOn[branch.name] === regions[branch.name]) continue;
        this.log.info(`branch '${branch.name}' ${regions[branch.name] ? "on" : "off"} (blueprint)`);
      }
      this.regionOn = regions;
      this.engine.setRegions(regions);
    }
    if (next.precision !== this.precision) {
      const wanted = next.precision;
      this.precision = wanted;
      this.log.info(`decoder precision -> ${wanted}`);
      // Serialized through the engine's own mutex, which then replays the current rig.
      void this.engine.setPrecision(prefersFp16(wanted))
        .catch((error) => this.log.error("precision swap failed:", error));
    }
  }

  debug(): PipelineDebug {
    return {
      kind: this.kind,
      branches: this.branches.map((branch) => branch.name),
      regions: { ...this.regionOn },
      rigDim: this.engine.rigDim,
      precision: this.precision,
      pushes: this.pushes,
      driven: !!this.poser,
      drivenTime: this.landed,
      refusedRigs: this.refused,
      constraints: this.constraints.report,
      worldScale: this.worldScale,
    };
  }

  /** The engine, the branches and the scene are the caller's — see the OgsPipeline note. */
  dispose(): void {
    this.disposed = true;
    this.poser = null;
  }
}
